import {useCallback, useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import SharedData from '../../common/SharedData'
import {getBulkJobNotifications, markAllBulkJobsSeen, IBulkJob} from './core/_requests'

/**
 * Header notification bell for async bulk-sheet results. Polls the supplier's
 * finished-but-unseen jobs; the badge shows the unread count and the dropdown
 * links to the Bulk Jobs tracking page.
 */
const KNOWN_STATUSES = [
  'queued',
  'processing',
  'completed',
  'completed_with_errors',
  'failed',
]

const BulkJobsBell = () => {
  const {currentUser, language} = SharedData()
  const {formatMessage} = useIntl()
  const navigate = useNavigate()
  const [count, setCount] = useState(0)
  const [items, setItems] = useState<IBulkJob[]>([])
  const [open, setOpen] = useState(false)

  const statusLabel = (s: string): string =>
    KNOWN_STATUSES.includes(s) ? formatMessage({id: `bulk_jobs.status.${s}`}) : s

  const load = useCallback(async () => {
    if (!currentUser?.token) return
    try {
      const res = await getBulkJobNotifications(currentUser.token, language)
      setCount(res.data?.unread_count ?? 0)
      setItems(res.data?.items ?? [])
    } catch {
      /* silent — bell must never break the header */
    }
  }, [currentUser?.token, language])

  useEffect(() => {
    void load()
    const t = setInterval(() => {
      void load()
    }, 30000)
    return () => clearInterval(t)
  }, [load])

  const goToJobs = async () => {
    setOpen(false)
    try {
      await markAllBulkJobsSeen(currentUser?.token, language)
    } catch {
      /* ignore */
    }
    setCount(0)
    setItems([])
    navigate('/products/bulk-jobs')
  }

  return (
    <div className='position-relative me-3'>
      <button
        type='button'
        className='btn btn-icon btn-active-light-primary position-relative'
        title={formatMessage({id: 'bulk_jobs.bell.title'})}
        onClick={() => setOpen((o) => !o)}
      >
        <i className='fa-solid fa-bell fs-3' />
        {count > 0 ? (
          <span className='position-absolute top-0 start-100 translate-middle badge badge-circle badge-danger'>
            {count}
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          className='card shadow-sm position-absolute end-0 mt-2'
          style={{width: 320, zIndex: 1050}}
        >
          <div className='card-body p-0'>
            {items.length === 0 ? (
              <div className='p-4 text-muted text-center'>
                {formatMessage({id: 'bulk_jobs.bell.empty'})}
              </div>
            ) : (
              <div className='mh-300px overflow-auto'>
                {items.map((it) => (
                  <button
                    key={it.job_id}
                    type='button'
                    className='btn text-start w-100 border-bottom py-3 px-4'
                    onClick={goToJobs}
                  >
                    <div className='fw-bold'>
                      {formatMessage(
                        {id: 'bulk_jobs.bell.item'},
                        {id: it.job_id, status: statusLabel(it.status)},
                      )}
                    </div>
                    <div className='text-muted fs-7'>{it.original_filename}</div>
                  </button>
                ))}
              </div>
            )}
            <button type='button' className='btn btn-light w-100' onClick={goToJobs}>
              {formatMessage({id: 'bulk_jobs.bell.view_all'})}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default BulkJobsBell
